<template>
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
    <!-- 订单信息 -->
    <div class="lg:col-span-2 space-y-6">
      <!-- 收货地址 -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-lg font-semibold mb-4 flex items-center">
          <i class="fa fa-map-marker text-primary mr-2"></i>
          收货地址
        </h3>
        
        <div v-if="!selectedAddress" class="border-2 border-dashed border-neutral-300 rounded-lg p-8 text-center">
          <button @click="showAddressModal = true" class="text-primary hover:underline">
            <i class="fa fa-plus-circle mr-1"></i> 添加收货地址
          </button>
        </div>
        
        <div v-else class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <p class="font-medium">{{ selectedAddress.name }} {{ selectedAddress.phone }}</p>
              <p class="text-neutral-500">{{ selectedAddress.province }} {{ selectedAddress.city }} {{ selectedAddress.district }} {{ selectedAddress.detail }}</p>
            </div>
            <button @click="showAddressModal = true" class="text-primary hover:underline text-sm">
              修改
            </button>
          </div>
          
          <div class="flex space-x-4">
            <button @click="showAddressModal = true" class="text-primary hover:underline text-sm">
              <i class="fa fa-plus-circle mr-1"></i> 添加新地址
            </button>
            <button class="text-neutral-500 hover:text-primary text-sm">
              <i class="fa fa-star-o mr-1"></i> 设为默认
            </button>
          </div>
        </div>
      </div>
      
      <!-- 支付方式 -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-lg font-semibold mb-4 flex items-center">
          <i class="fa fa-credit-card text-primary mr-2"></i>
          支付方式
        </h3>
        
        <div class="space-y-3">
          <label class="flex items-center p-3 border border-neutral-200 rounded-lg cursor-pointer hover:border-primary transition-colors">
            <input type="radio" name="payment" v-model="paymentMethod" value="wechat" class="mr-3">
            <div class="flex items-center w-full">
              <i class="fa fa-weixin text-[#07C160] text-xl mr-3"></i>
              <span>微信支付</span>
              <div class="ml-auto text-neutral-400 text-sm">推荐</div>
            </div>
          </label>
          
          <label class="flex items-center p-3 border border-neutral-200 rounded-lg cursor-pointer hover:border-primary transition-colors">
            <input type="radio" name="payment" v-model="paymentMethod" value="alipay" class="mr-3">
            <div class="flex items-center w-full">
              <i class="fa fa-alipay text-[#1677FF] text-xl mr-3"></i>
              <span>支付宝</span>
            </div>
          </label>
          
          <label class="flex items-center p-3 border border-neutral-200 rounded-lg cursor-pointer hover:border-primary transition-colors">
            <input type="radio" name="payment" v-model="paymentMethod" value="card" class="mr-3">
            <div class="flex items-center w-full">
              <i class="fa fa-credit-card text-[#666] text-xl mr-3"></i>
              <span>银行卡支付</span>
            </div>
          </label>
        </div>
      </div>
      
      <!-- 订单备注 -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-lg font-semibold mb-4 flex items-center">
          <i class="fa fa-pencil text-primary mr-2"></i>
          订单备注
        </h3>
        
        <textarea v-model="orderNote" 
                  rows="3" 
                  class="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  placeholder="请输入订单备注（选填）"></textarea>
      </div>
    </div>
    
    <!-- 订单摘要 -->
    <div class="bg-white rounded-lg shadow-md p-6 sticky top-24">
      <h3 class="text-lg font-semibold mb-4">订单摘要</h3>
      
      <div class="border-b border-neutral-200 pb-4 mb-4">
        <div class="flex justify-between mb-2">
          <span>商品总价</span>
          <span>¥{{ orderSummary.subtotal }}</span>
        </div>
        <div class="flex justify-between mb-2">
          <span>运费</span>
          <span>{{ orderSummary.shippingFee === 0 ? '免费' : '¥' + orderSummary.shippingFee }}</span>
        </div>
        <div class="flex justify-between mb-2">
          <span>优惠折扣</span>
          <span class="text-danger">-¥{{ orderSummary.discount }}</span>
        </div>
      </div>
      
      <div class="flex justify-between font-bold text-lg mb-6">
        <span>实付金额</span>
        <span class="text-primary">¥{{ orderSummary.total }}</span>
      </div>
      
      <button @click="submitOrder" 
              class="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg transition-colors mb-4">
        提交订单并支付
      </button>
      
      <div class="text-sm text-neutral-500 text-center">
        <p>提交订单即表示您同意我们的</p>
        <p>
          <router-link to="/terms-of-service" class="text-primary hover:underline">用户条款</router-link> 和
          <router-link to="/privacy-policy" class="text-primary hover:underline">隐私政策</router-link>
        </p>
      </div>
    </div>
    
    <!-- 地址选择/添加弹窗 -->
    <div v-if="showAddressModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 mx-4" v-click-outside="closeAddressModal">
        <h3 class="text-lg font-semibold mb-4">选择/添加收货地址</h3>
        
        <div class="space-y-4 mb-6">
          <!-- 地址列表 -->
          <div v-for="address in addressList" :key="address.id" class="border border-neutral-200 rounded-lg p-3 cursor-pointer hover:border-primary">
            <div class="flex items-center justify-between">
              <div>
                <p class="font-medium">{{ address.name }} {{ address.phone }}</p>
                <p class="text-neutral-500 text-sm">{{ address.province }} {{ address.city }} {{ address.district }} {{ address.detail }}</p>
              </div>
              <input type="radio" name="address" v-model="selectedAddressId" :value="address.id">
            </div>
          </div>
          
          <!-- 添加新地址按钮 -->
          <button @click="isAddAddress = true" class="w-full border border-dashed border-neutral-300 rounded-lg p-3 text-primary hover:border-primary transition-colors">
            <i class="fa fa-plus-circle mr-1"></i> 添加新地址
          </button>
        </div>
        
        <!-- 新地址表单 (按需显示) -->
        <div v-if="isAddAddress" class="space-y-4 mb-6">
          <div>
            <label class="block text-sm font-medium mb-1">收货人姓名</label>
            <input type="text" v-model="newAddress.name" class="w-full px-3 py-2 border border-neutral-200 rounded-lg">
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">手机号码</label>
            <input type="tel" v-model="newAddress.phone" class="w-full px-3 py-2 border border-neutral-200 rounded-lg">
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div>
              <label class="block text-sm font-medium mb-1">省份</label>
              <select v-model="newAddress.province" class="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                <option value="">请选择</option>
                <option value="北京市">北京市</option>
                <option value="上海市">上海市</option>
                <option value="广东省">广东省</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">城市</label>
              <select v-model="newAddress.city" class="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                <option value="">请选择</option>
                <option value="北京市">北京市</option>
                <option value="上海市">上海市</option>
                <option value="广州市">广州市</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">区县</label>
              <select v-model="newAddress.district" class="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                <option value="">请选择</option>
                <option value="朝阳区">朝阳区</option>
                <option value="海淀区">海淀区</option>
              </select>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">详细地址</label>
            <input type="text" v-model="newAddress.detail" class="w-full px-3 py-2 border border-neutral-200 rounded-lg">
          </div>
        </div>
        
        <!-- 操作按钮 -->
        <div class="flex space-x-3">
          <button @click="closeAddressModal" class="flex-1 py-2 px-4 border border-neutral-200 rounded-lg hover:bg-neutral-100 transition-colors">
            取消
          </button>
          <button @click="confirmAddress" class="flex-1 py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
            确认
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Checkout',
  data() {
    return {
      // 地址相关
      showAddressModal: false,
      isAddAddress: false,
      selectedAddressId: '',
      addressList: [
        { id: 1, name: '张三', phone: '13800138000', province: '北京市', city: '北京市', district: '朝阳区', detail: '建国路88号8号楼808室' },
        { id: 2, name: '张三', phone: '13800138000', province: '上海市', city: '上海市', district: '浦东新区', detail: '张江高科技园区博云路2号' }
      ],
      newAddress: {
        name: '',
        phone: '',
        province: '',
        city: '',
        district: '',
        detail: ''
      },
      
      // 支付方式
      paymentMethod: 'wechat',
      
      // 订单备注
      orderNote: '',
      
      // 订单摘要
      orderSummary: {
        subtotal: 598.00,
        shippingFee: 0,
        discount: 50.00,
        total: 548.00
      }
    }
  },
  computed: {
    selectedAddress() {
      return this.addressList.find(addr => addr.id === this.selectedAddressId) || null
    }
  },
  methods: {
    // 打开地址弹窗
    openAddressModal() {
      this.showAddressModal = true
    },
    
    // 关闭地址弹窗
    closeAddressModal() {
      this.showAddressModal = false
      this.isAddAddress = false
    },
    
    // 确认地址选择/添加
    confirmAddress() {
      if (this.isAddAddress) {
        // 添加新地址
        if (this.newAddress.name && this.newAddress.phone && this.newAddress.detail) {
          const newId = Math.max(...this.addressList.map(addr => addr.id)) + 1
          this.addressList.push({
            id: newId,
            ...this.newAddress
          })
          this.selectedAddressId = newId
          this.isAddAddress = false
          this.newAddress = {
            name: '',
            phone: '',
            province: '',
            city: '',
            district: '',
            detail: ''
          }
        }
      }
      
      this.showAddressModal = false
    },
    
    // 提交订单
    submitOrder() {
      if (!this.selectedAddressId) {
        this.$bus.$emit('showMessage', '请选择收货地址')
        return
      }
      
      // 模拟提交订单
      this.$router.push('/payment-success')
      this.$bus.$emit('showMessage', '订单提交成功，跳转到支付页面...')
    }
  }
}
</script>